import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AutoAwesomeOutlined } from '@mui/icons-material';
import { SettingsIcon, FolderIcon, FaxIcon } from 'assets';
import { useTranslation } from 'react-i18next';

import { FC } from 'react';
import { isHipaaLink } from '../../config/productProfile';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';

const StyledStack = styled(Stack)(({ theme }) => ({
  position: 'relative',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  height: 80,
  width: 80,
  boxSizing: 'border-box',
  paddingInline: '12px',
  borderRadius: '10px',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.palette.primary.main,
  background: theme.palette.background.paper,
  overflow: 'visible',
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'none',
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  display: 'block',
  margin: '0 auto',
  textAlign: 'center',
  color: theme.palette.text.primary,
  fontFamily: 'Open Sans',
  fontSize: '10px',
  fontWeight: 700,
  lineHeight: '13.62px',
  wordBreak: 'normal',
  overflowWrap: 'normal',
  hyphens: 'none',
  whiteSpace: 'normal',
}));

const LabelWrapper = styled(Stack)({
  display: 'flex',
  minHeight: 28,
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  paddingInline: 0,
});

export type AINotesMenuProps = {
  aiCreditsCount?: number;
  confirmNavigation?: () => boolean;
};

export const AINotesMenu: FC<AINotesMenuProps> = ({ confirmNavigation }) => {
  const { t, i18n } = useTranslation('providerRoom');
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const isMember = user?.clinicUser?.via3UserType === 'Member';
  const canAccessFaxes = !!user?.clinicUser?.canAccessFaxes && !isMember;
  const isChineseLanguage = i18n.resolvedLanguage?.startsWith('zh');
  const isEnglishLocale = i18n.resolvedLanguage?.toLowerCase().startsWith('en') ?? false;
  const compactTypographySx = isEnglishLocale ? undefined : { fontSize: '9px', lineHeight: '12px' };
  const tileSx = { width: 84, minWidth: 84, maxWidth: 84, flex: '0 0 84px' } as const;
  const shouldCompactMenu = !isHipaaLink && !canAccessFaxes;
  const canNavigateAway = (): boolean => {
    return confirmNavigation ? confirmNavigation() : true;
  };

  const handleNavigateToAINotesGenerator = () => {
    if (canNavigateAway()) {
      navigate('/settings#notesGenerator');
    }
  };

  const handleNavigateToFaxing = () => {
    if (canNavigateAway()) {
      navigate('/settings#faxes');
    }
  };

  const handleNavigateToDocuments = () => {
    if (canNavigateAway()) {
      navigate('/settings#documents');
    }
  };

  const handleNavigateToSettings = () => {
    if (canNavigateAway()) {
      navigate('/settings');
    }
  };

  return (
    <>
      <Stack
        direction='row'
        alignItems='start'
        justifyContent={shouldCompactMenu ? 'flex-start' : 'space-between'}
        sx={({ breakpoints }) => ({
          gridColumnStart: 1,
          gridColumnEnd: 2,
          gridRowStart: 2,
          gridRowEnd: 3,
          ...(shouldCompactMenu ? { gap: '19px' } : {}),
          [breakpoints.down(900)]: {
            justifyContent: 'center',
            gap: '19px',
          },
        })}
      >
        {isHipaaLink ? (
          <StyledStack gap='4px' onClick={handleNavigateToAINotesGenerator} sx={{ position: 'relative', ...tileSx }}>
            <AutoAwesomeOutlined sx={(theme) => ({ width: 24, height: 24, color: theme.palette.primary.main })} />
            <LabelWrapper>
              <StyledTypography align='center' sx={compactTypographySx}>
                {t('aiNotesGenerator')}
              </StyledTypography>
            </LabelWrapper>
            <Box
              sx={(theme) => ({
                position: 'absolute',
                bottom: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: theme.palette.primary.main,
                color: '#ffffff',
                fontSize: '10px',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '6px',
                border: '1px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                textTransform: 'uppercase',
                zIndex: 1,
              })}
            >
              {t('new')}
            </Box>
          </StyledStack>
        ) : canAccessFaxes ? (
          <StyledStack gap='2px' onClick={handleNavigateToFaxing} sx={tileSx}>
            <FaxIcon
              sx={(theme) => ({
                width: 24,
                height: 24,
                transform: 'scale(1.12)',
                '&.MuiSvgIcon-root path': {
                  fill: theme.palette.primary.main,
                },
              })}
            />
            <LabelWrapper>
              <StyledTypography align='center' sx={compactTypographySx}>
                {t('online')}
                <br />
                {t('faxes')}
              </StyledTypography>
            </LabelWrapper>
          </StyledStack>
        ) : null}
        <StyledStack gap='2px' onClick={handleNavigateToDocuments} sx={tileSx}>
          <FolderIcon
            sx={(theme) => ({
              width: 24,
              height: 24,
              transform: 'scale(1.12)',
              '&.MuiSvgIcon-root path': {
                fill: theme.palette.primary.main,
              },
            })}
          />
          <LabelWrapper>
            <StyledTypography align='center' sx={compactTypographySx}>
              {isChineseLanguage ? (
                isHipaaLink ? (
                  `${t('my')} ${t('documents')}`
                ) : (
                  `${t('shared')} ${t('documents')}`
                )
              ) : isHipaaLink ? (
                <>
                  {t('my')}
                  <br />
                  {t('documents')}
                </>
              ) : (
                <>
                  {t('shared')}
                  <br />
                  {t('documents')}
                </>
              )}
            </StyledTypography>
          </LabelWrapper>
        </StyledStack>
        <StyledStack gap='2px' onClick={handleNavigateToSettings} sx={tileSx}>
          <SettingsIcon
            sx={(theme) => ({
              width: 24,
              height: 24,
              '&.MuiSvgIcon-root path': { fill: theme.palette.primary.main },
            })}
          />
          <LabelWrapper>
            <StyledTypography align='center' sx={compactTypographySx}>
              {t('accountSettings')}
            </StyledTypography>
          </LabelWrapper>
        </StyledStack>
      </Stack>
    </>
  );
};
